import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import NotificationBell from "@/components/base/NotificationBell";
import ConfirmDialog from "@/components/base/ConfirmDialog";
import ToastContainer from "@/components/base/Toast";
import { useNotifications } from "@/context/NotificationContext";
import ThemeToggle from "@/components/base/ThemeToggle";
import LangToggle from "@/components/base/LangToggle";

interface AdminLayoutProps {
  children: React.ReactNode;
}

export default function AdminLayout({ children }: AdminLayoutProps) {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  useNotifications(); // ensure context is available
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [logoutDialog, setLogoutDialog] = useState(false);

  const navGroups = [
    {
      label: "Main",
      items: [
        { label: "Overview", icon: "ri-dashboard-line", href: "/admin" },
        { label: "Clients", icon: "ri-user-3-line", href: "/admin/clients" },
        { label: "Projects", icon: "ri-folder-line", href: "/admin/projects" },
        { label: "Tickets", icon: "ri-customer-service-line", href: "/admin/tickets" },
        { label: "Leads", icon: "ri-user-add-line", href: "/admin/leads" },
        { label: "Invoices", icon: "ri-file-list-3-line", href: "/admin/invoices" },
      ],
    },
    {
      label: "Team & Content",
      items: [
        { label: "Team", icon: "ri-team-line", href: "/admin/team" },
        { label: "Portfolio", icon: "ri-gallery-line", href: "/admin/portfolio" },
      ],
    },
    ...(user?.role === "admin" ? [{
      label: "System",
      items: [
        { label: "Settings", icon: "ri-settings-3-line", href: "/admin/settings" },
      ],
    }] : []),
  ];

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const initials = user?.name?.split(" ").map((n) => n[0]).join("").toUpperCase().slice(0, 2) || "A";

  const convertDriveLink = (url: string) => {
    if (!url) return "";
    if (url.includes("drive.google.com")) {
      const fileMatch = url.match(/\/file\/d\/([^\/]+)/);
      if (fileMatch) {
        return `https://drive.google.com/thumbnail?id=${fileMatch[1]}&sz=w200`;
      }
      const idMatch = url.match(/[?&]id=([^&]+)/);
      if (idMatch) {
        return `https://drive.google.com/thumbnail?id=${idMatch[1]}&sz=w200`;
      }
    }
    return url;
  };

  const avatarUrl = convertDriveLink((user as any)?.avatarUrl || "");

  const isActive = (href: string) =>
    href === "/admin"
      ? location.pathname === "/admin"
      : location.pathname.startsWith(href);

  return (
    <div className="min-h-screen bg-deep-black flex font-inter">
      {/* Sidebar */}
      <aside
        className={`fixed top-0 left-0 h-full w-60 bg-dark-panel border-r border-dark-border z-40 flex flex-col transition-transform duration-300 ${
          sidebarOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"
        }`}
      >
        {/* Logo */}
        <div className="flex items-center gap-3 px-5 py-4 border-b border-dark-border">
          <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-cyber-red/10 border border-cyber-red/20 flex-shrink-0">
            <i className="ri-shield-flash-line text-cyber-red text-lg" />
          </div>
          <div>
            <div className="font-black text-white text-sm tracking-widest" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
              SCHEMACKOZO
            </div>
            <div className="text-cyber-red text-xs font-semibold uppercase tracking-widest leading-none mt-0.5">
              Admin Panel
            </div>
          </div>
        </div>

        {/* User Card */}
        <div className="mx-3 my-3 p-3 rounded-xl bg-dark-card border border-dark-border">
          <div className="flex items-center gap-2.5">
            {avatarUrl ? (
              <img
                src={avatarUrl}
                alt="Avatar"
                className="w-9 h-9 rounded-full object-cover border border-cyber-red/30 flex-shrink-0"
                referrerPolicy="no-referrer"
              />
            ) : (
              <div className="w-9 h-9 flex items-center justify-center rounded-full bg-gradient-to-br from-cyber-red/30 to-cyber-red/10 border border-cyber-red/30 text-cyber-red text-sm font-black flex-shrink-0">
                {initials}
              </div>
            )}
            <div className="min-w-0 flex-1">
              <div className="text-white text-xs font-bold truncate">{user?.name || "Admin"}</div>
              <div className="flex items-center gap-1 mt-0.5">
                <span className="w-1.5 h-1.5 rounded-full bg-cyber-green" />
                <span className="text-cyber-red text-xs capitalize font-medium">{user?.role || "admin"}</span>
              </div>
            </div>
            <Link to="/dashboard/profile" className="w-6 h-6 flex items-center justify-center text-silver-dim hover:text-white transition-colors cursor-pointer flex-shrink-0">
              <i className="ri-settings-4-line text-sm" />
            </Link>
          </div>
        </div>

        {/* Nav */}
        <nav className="flex-1 px-3 py-1 overflow-y-auto space-y-4">
          {navGroups.map((group) => (
            <div key={group.label}>
              <p className="text-silver-dim text-xs font-semibold uppercase tracking-widest px-2 mb-1.5">{group.label}</p>
              <div className="space-y-0.5">
                {group.items.map((item) => {
                  const active = isActive(item.href);
                  return (
                    <Link
                      key={item.href}
                      to={item.href}
                      onClick={() => setSidebarOpen(false)}
                      className={`flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all duration-200 cursor-pointer group ${
                        active
                          ? "bg-cyber-red/10 text-cyber-red border border-cyber-red/20"
                          : "text-silver hover:text-white hover:bg-dark-card border border-transparent"
                      }`}
                    >
                      <div className={`w-4 h-4 flex items-center justify-center flex-shrink-0 ${active ? "text-cyber-red" : "text-silver-dim group-hover:text-white"}`}>
                        <i className={`${item.icon} text-sm`} />
                      </div>
                      <span className="flex-1">{item.label}</span>
                      {active && <span className="w-1.5 h-1.5 rounded-full bg-cyber-red flex-shrink-0" />}
                    </Link>
                  );
                })}
              </div>
            </div>
          ))}
        </nav>

        {/* Bottom Links */}
        <div className="px-3 py-3 border-t border-dark-border space-y-0.5">
          <Link
            to="/"
            className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm text-silver hover:text-white hover:bg-dark-card border border-transparent transition-all cursor-pointer"
          >
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-global-line text-sm text-silver-dim" />
            </div>
            Public Website
          </Link>
          <Link
            to="/dashboard"
            className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm text-silver hover:text-white hover:bg-dark-card border border-transparent transition-all cursor-pointer"
          >
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-user-line text-sm text-silver-dim" />
            </div>
            Client View
          </Link>
          <button
            onClick={() => setLogoutDialog(true)}
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm text-silver hover:text-cyber-red hover:bg-cyber-red/5 border border-transparent transition-all cursor-pointer"
          >
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-logout-box-line text-sm" />
            </div>
            Sign Out
          </button>
        </div>
      </aside>

      {/* Mobile overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/60 backdrop-blur-sm z-30 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Main Content */}
      <div className="flex-1 lg:ml-60 flex flex-col min-h-screen">
        {/* Top Header */}
        <header className="sticky top-0 z-20 bg-deep-black/95 backdrop-blur-md border-b border-dark-border px-4 md:px-6 h-14 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              className="lg:hidden w-9 h-9 flex items-center justify-center rounded-lg border border-dark-border text-silver hover:text-white cursor-pointer transition-colors"
              onClick={() => setSidebarOpen(true)}
            >
              <i className="ri-menu-line text-lg" />
            </button>
            {/* Breadcrumb */}
            <div className="hidden lg:flex items-center gap-2 text-sm">
              <span className="text-silver-dim">Admin</span>
              <i className="ri-arrow-right-s-line text-silver-dim text-xs" />
              <span className="text-white font-medium capitalize">
                {location.pathname.split("/").filter(Boolean).slice(1).join(" / ") || "Dashboard"}
              </span>
            </div>
          </div>

          <div className="flex items-center gap-2">
            {/* Quick actions */}
            <Link
              to="/admin/tickets"
              className="hidden sm:flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-dark-border text-silver-dim hover:text-white hover:border-electric-blue/30 text-xs transition-all cursor-pointer whitespace-nowrap"
            >
              <i className="ri-customer-service-line text-xs" />
              Tickets
            </Link>
            <Link
              to="/admin/leads"
              className="hidden sm:flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-dark-border text-silver-dim hover:text-white hover:border-electric-blue/30 text-xs transition-all cursor-pointer whitespace-nowrap"
            >
              <i className="ri-user-add-line text-xs" />
              New Lead
            </Link>
            <Link
              to="/admin/projects"
              className="hidden sm:flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-dark-border text-silver-dim hover:text-white hover:border-electric-blue/30 text-xs transition-all cursor-pointer whitespace-nowrap"
            >
              <i className="ri-add-line text-xs" />
              New Project
            </Link>
            <LangToggle />
            <ThemeToggle />
            <NotificationBell />
            {avatarUrl ? (
              <img
                src={avatarUrl}
                alt="Avatar"
                className="w-8 h-8 rounded-full object-cover border border-cyber-red/20 cursor-default"
                referrerPolicy="no-referrer"
              />
            ) : (
              <div className="w-8 h-8 flex items-center justify-center rounded-full bg-cyber-red/10 border border-cyber-red/20 text-cyber-red text-xs font-black cursor-default">
                {initials}
              </div>
            )}
          </div>
        </header>

        {/* Page Content */}
        <main className="flex-1 p-4 md:p-6">
          {children}
        </main>

        {/* Footer */}
        <footer className="border-t border-dark-border px-6 py-3 flex items-center justify-between">
          <p className="text-silver-dim text-xs">Schemackozo Admin &copy; {new Date().getFullYear()}</p>
          <div className="flex items-center gap-1.5">
            <span className="w-1.5 h-1.5 rounded-full bg-cyber-green" />
            <span className="text-silver-dim text-xs">All systems operational</span>
          </div>
        </footer>
      </div>

      {/* Toast Notifications */}
      <ToastContainer />

      {/* Logout Dialog */}
      <ConfirmDialog
        open={logoutDialog}
        title="Sign Out"
        message="Are you sure you want to sign out of the admin panel?"
        confirmLabel="Sign Out"
        cancelLabel="Stay"
        danger
        onConfirm={handleLogout}
        onCancel={() => setLogoutDialog(false)}
      />
    </div>
  );
}
